import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import jsPDF from 'jspdf';

function JobRequestSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  
  const {
    jobId,
    email,
    clientName,
    clientCompany,
    clientPhone,
    clientRef,
    caseTitle,
    caseNumber,
    courtLocation,
    courtDate,
    priority,
    recipientType,
    partyToServe,
    files,
    addresses,
    addressCount,
    pricing,
    submittedAt
  } = location.state || {};

  useEffect(() => {
    // If no state data, redirect to home
    if (!email) {
      navigate('/');
    }
  }, [email, navigate]);

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }) + ' CST';
  };

  const generatePDF = () => {
    setGenerating(true);
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let y = 20;
      const leftMargin = 20;
      const lineHeight = 7;
      
      // Header
      doc.setFillColor(237, 28, 36); // WesTex Red
      doc.rect(0, 0, pageWidth, 35, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('WesTex Legal', pageWidth / 2, 18, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Process Serving Request Confirmation', pageWidth / 2, 28, { align: 'center' });
      
      y = 50;
      doc.setTextColor(0, 0, 0);
      
      // Confirmation Box
      doc.setFillColor(40, 167, 69);
      doc.roundedRect(leftMargin, y, pageWidth - 40, 20, 3, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('REQUEST RECEIVED', pageWidth / 2, y + 13, { align: 'center' });
      
      y += 30;
      doc.setTextColor(0, 0, 0);
      
      // Submission Details
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Submission Details', leftMargin, y);
      y += lineHeight;
      
      doc.setDrawColor(237, 28, 36);
      doc.line(leftMargin, y, pageWidth - leftMargin, y);
      y += lineHeight;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      const addField = (label, value) => {
        if (!value || value === 'N/A') return;
        doc.setFont('helvetica', 'bold');
        doc.text(`${label}:`, leftMargin, y);
        doc.setFont('helvetica', 'normal');
        doc.text(String(value), leftMargin + 45, y);
        y += lineHeight;
      };
      
      addField('Submitted', formatDate(submittedAt));
      
      y += 5;
      
      // Client Information
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Client Information', leftMargin, y);
      y += lineHeight;
      doc.setDrawColor(237, 28, 36);
      doc.line(leftMargin, y, pageWidth - leftMargin, y);
      y += lineHeight;
      
      doc.setFontSize(10);
      addField('Name', clientName);
      addField('Company', clientCompany);
      addField('Email', email);
      addField('Phone', clientPhone);
      if (clientRef) addField('Reference #', clientRef);
      
      y += 5;
      
      // Case Information
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Case Information', leftMargin, y);
      y += lineHeight;
      doc.setDrawColor(237, 28, 36);
      doc.line(leftMargin, y, pageWidth - leftMargin, y);
      y += lineHeight;
      
      doc.setFontSize(10);
      addField('Case', caseTitle);
      addField('Case Number', caseNumber);
      addField('Court', courtLocation);
      if (courtDate) addField('Court Date', courtDate);
      addField('Priority', priority);
      
      y += 5;
      
      // Recipient Information
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Service Information', leftMargin, y);
      y += lineHeight;
      doc.setDrawColor(237, 28, 36);
      doc.line(leftMargin, y, pageWidth - leftMargin, y);
      y += lineHeight;
      
      doc.setFontSize(10);
      addField('Party to Serve', partyToServe);
      addField('Type', recipientType === 'individual' ? 'Individual' : 'Business Entity');
      
      // Addresses
      if (addresses && addresses.length > 0) {
        y += 3;
        doc.setFont('helvetica', 'bold');
        doc.text('Service Address(es):', leftMargin, y);
        y += lineHeight;
        
        doc.setFont('helvetica', 'normal');
        addresses.forEach((addr, index) => {
          const prefix = addresses.length > 1 ? `${index + 1}. ` : '';
          doc.text(`${prefix}${addr.street}`, leftMargin + 5, y);
          y += lineHeight - 2;
          doc.text(`${addr.city}, ${addr.state} ${addr.zip}`, leftMargin + 5, y);
          y += lineHeight;
        });
      }
      
      y += 5;
      
      // Documents
      if (files && files.length > 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Documents Uploaded', leftMargin, y);
        y += lineHeight;
        doc.setDrawColor(237, 28, 36);
        doc.line(leftMargin, y, pageWidth - leftMargin, y);
        y += lineHeight;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        files.forEach((file, index) => {
          const size = (file.size / 1024 / 1024).toFixed(2);
          doc.text(`• ${file.name} (${size} MB)`, leftMargin + 5, y);
          y += lineHeight;
        });
      }
      
      // Footer
      const footerY = doc.internal.pageSize.getHeight() - 25;
      doc.setDrawColor(200, 200, 200);
      doc.line(leftMargin, footerY, pageWidth - leftMargin, footerY);
      
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text('WesTex Legal | Professional Process Serving', pageWidth / 2, footerY + 8, { align: 'center' });
      doc.text('(806) 401-1749 | WesTexLegal@gmail.com', pageWidth / 2, footerY + 14, { align: 'center' });
      
      // Save the PDF
      const filename = `WesTex_Receipt_${caseNumber || jobId?.substring(0, 8) || 'submission'}.pdf`;
      doc.save(filename);
      
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('There was an error generating the PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  if (!email) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-4 py-16">
        {/* Success Icon */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-600 rounded-full mb-6 shadow-xl">
            <svg className="w-14 h-14 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-3 text-white">Submission Received</h1>
          <p className="text-xl text-gray-400">Thank you for your process serving request</p>
        </div>

        {/* Details Card */}
        <div className="bg-gray-900 rounded-lg p-8 border border-gray-700 mb-6 shadow-lg">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400 mb-1">📧 Confirmation sent to:</p>
                <p className="text-lg font-semibold text-red-500">{email}</p>
              </div>
            </div>

            {caseTitle && (
              <div>
                <p className="text-sm text-gray-400 mb-1">📋 Case:</p>
                <p className="text-lg">{caseTitle}</p>
                {caseNumber && <p className="text-sm text-gray-500">Case #: {caseNumber}</p>}
              </div>
            )}

            {partyToServe && (
              <div>
                <p className="text-sm text-gray-400 mb-1">👤 Party to Serve:</p>
                <p className="text-lg">{partyToServe}</p>
              </div>
            )}

            {addresses && addresses.length > 0 && (
              <div>
                <p className="text-sm text-gray-400 mb-2">📍 Service Location{addresses.length > 1 ? 's' : ''}:</p>
                {addresses.map((addr, index) => (
                  <div key={index} className="mb-2">
                    <p className="text-base">
                      {addresses.length > 1 && <span className="text-gray-500 mr-2">{index + 1}.</span>}
                      {addr.street}, {addr.city}, {addr.state} {addr.zip}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {files && files.length > 0 && (
              <div>
                <p className="text-sm text-gray-400 mb-2">📎 Documents Uploaded ({files.length}):</p>
                <ul className="space-y-1">
                  {files.map((file, index) => (
                    <li key={index} className="text-sm flex items-center">
                      <svg className="h-4 w-4 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                      </svg>
                      <span>{file.name}</span>
                      <span className="text-gray-500 ml-2">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {priority && (
              <div>
                <p className="text-sm text-gray-400 mb-1">⚡ Priority:</p>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                  priority === 'Rush' ? 'bg-red-600/20 text-red-400' : 'bg-gray-700 text-gray-300'
                }`}>
                  {priority} Service
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Download Receipt Button */}
        <div className="bg-gray-900 rounded-lg p-6 border border-gray-700 mb-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white mb-1">Download Receipt</h3>
              <p className="text-sm text-gray-400">Save a PDF copy for your records</p>
            </div>
            <button
              onClick={generatePDF}
              disabled={generating}
              className="px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-wait rounded-lg font-semibold transition-all shadow-md hover:shadow-lg flex items-center gap-2"
            >
              {generating ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download PDF
                </>
              )}
            </button>
          </div>
        </div>

        {/* Client Portal Prompt */}
        <div className="bg-gradient-to-r from-blue-900/50 to-indigo-900/50 rounded-lg p-6 border border-blue-700/50 mb-6 shadow-lg">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-600/20 rounded-full">
                <svg className="h-8 w-8 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">Want to track this job?</h3>
                <p className="text-sm text-blue-200">
                  Create a free Client Portal account to track job status, view history, and get faster submissions.
                </p>
              </div>
            </div>
            <Link
              to="/portal/register"
              className="whitespace-nowrap px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Create Free Account
            </Link>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-gray-900 rounded-lg p-8 border border-gray-700 mb-6 shadow-lg">
          <h2 className="text-2xl font-bold mb-4 text-white">What Happens Next?</h2>
          <p className="text-gray-300 text-lg mb-5">
            We&apos;ll contact you within 24 hours to confirm pricing and availability.
          </p>
          <div className="flex items-center gap-3 text-gray-400">
            <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            <span>Questions? Call <a href="tel:8064011749" className="text-red-500 hover:text-red-400 font-semibold">(806) 401-1749</a></span>
          </div>
        </div>

        {/* Multiple Defendants Warning */}
        <div className="bg-gray-900 border-l-4 border-red-600 rounded-lg p-6 mb-8 shadow-lg">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-white mb-2">Serving Multiple Defendants?</h3>
            <p className="text-sm text-gray-300 mb-4">
              Remember: Each defendant requires a separate submission.
            </p>
            <Link
              to="/job-request"
              className="inline-block px-6 py-2.5 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg"
            >
              Submit Another Job Request
            </Link>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center">
          <Link
            to="/"
            className="px-10 py-3 bg-gray-800 hover:bg-gray-700 border border-gray-600 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg"
          >
            Return to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default JobRequestSuccess;
